import { useEffect, useState } from 'react';
import { closePushApi, type PushDryRunResponse, type PushLiveResponse } from '../services/closePushApi';
import type { RosterSelection } from './useRosterSelection';

// Two ways to open this modal:
//   - Single-source (AOI or pool order roster selection): the operator
//     ticked a row set on one page and clicked Push. RosterSelection
//     carries the party_ids; source_kind + source_id are fixed.
//   - Lead-list bulk push: each lead_list_item_id may resolve to a
//     different (party_id, source_kind, source_id). The modal still
//     opens with a count + the briefs-per-party preview, but the
//     payload sent to the API is { source_kind: 'lead_list',
//     lead_list_item_ids }. The dry-run preview shows each brief with
//     its OWN source tag/label since per-party context is preserved.
interface PushConfirmModalPropsSingleSource {
  source_kind: 'aoi' | 'pool_order';
  source_id: string;
  selection: RosterSelection;
  onClose: () => void;
  onPushed: (result: PushLiveResponse) => void;
}
interface PushConfirmModalPropsLeadList {
  source_kind: 'lead_list';
  lead_list_item_ids: string[];
  // Display only. Selection state is owned by the LeadList page.
  count: number;
  onClose: () => void;
  onPushed: (result: PushLiveResponse) => void;
}
type PushConfirmModalProps = PushConfirmModalPropsSingleSource | PushConfirmModalPropsLeadList;

// Three-phase modal: dry-run preview → live push → success state.
// Phase 1 runs immediately on open: dry-run hits the close-push endpoint
// with options.dry_run=true, returns the per-party briefs + summary
// without any Close side effects. Operator scans + clicks "Push N to
// Close" to commit.

type Phase = 'previewing' | 'preview-ready' | 'pushing' | 'pushed' | 'error';

export function PushConfirmModal(props: PushConfirmModalProps) {
  const { source_kind, onClose, onPushed } = props;
  const [phase, setPhase] = useState<Phase>('previewing');
  const [err, setErr] = useState<string | null>(null);
  const [preview, setPreview] = useState<PushDryRunResponse | null>(null);
  const [pushed, setPushed] = useState<PushLiveResponse | null>(null);
  const [openBriefPartyId, setOpenBriefPartyId] = useState<string | null>(null);

  // Selection count + payload shape depend on which props variant we got.
  const count = source_kind === 'lead_list'
    ? (props as PushConfirmModalPropsLeadList).count
    : (props as PushConfirmModalPropsSingleSource).selection.count;

  function buildPayload(): any {
    if (source_kind === 'lead_list') {
      const p = props as PushConfirmModalPropsLeadList;
      return { source_kind, lead_list_item_ids: p.lead_list_item_ids };
    }
    const p = props as PushConfirmModalPropsSingleSource;
    return {
      source_kind,
      source_id: p.source_id,
      party_ids: p.selection.selectedRows.map(r => r.party_id),
    };
  }

  useEffect(() => {
    (async () => {
      try {
        const r = await closePushApi.dryRun(buildPayload());
        setPreview(r);
        setOpenBriefPartyId(r.briefs[0]?.party_id ?? null);
        setPhase('preview-ready');
      } catch (e: any) {
        setErr(e?.message ?? 'Preview failed');
        setPhase('error');
      }
    })();
  }, []);

  async function commitPush() {
    setPhase('pushing');
    setErr(null);
    try {
      const r = await closePushApi.push(buildPayload());
      setPushed(r);
      setPhase('pushed');
      onPushed(r);
    } catch (e: any) {
      setErr(e?.message ?? 'Push failed');
      setPhase('error');
    }
  }

  const noContactCount = preview?.summary.without_contact ?? 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="bg-bg-card border border-border rounded-lg w-[820px] max-w-[94vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="px-6 py-5 border-b border-border flex items-center justify-between shrink-0">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-text-faint">push to close</div>
            <div className="font-display text-[22px] leading-tight">{phase === 'pushed' ? 'Pushed' : 'Confirm'} <em className="text-accent not-italic">{count} {count === 1 ? 'lead' : 'leads'}</em></div>
          </div>
          <button onClick={onClose} className="text-text-dim hover:text-text text-2xl leading-none">×</button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-5">
          {phase === 'previewing' && (
            <div className="text-[12px] text-text-faint font-mono py-12 text-center">
              Generating per-party briefs… <span className="text-text-dim">(no Close calls yet)</span>
            </div>
          )}

          {phase === 'error' && (
            <div className="text-[13px] text-red bg-red/10 border border-red/40 rounded px-4 py-3 my-4">
              {err}
            </div>
          )}

          {phase === 'preview-ready' && preview && (
            <>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div className="bg-bg-input/40 border border-border rounded px-4 py-3 text-[12px]">
                  <div className="text-[10px] uppercase tracking-wider text-text-faint font-mono mb-1.5">Close write plan</div>
                  {/* Split the count when the server told us how many are
                      already in Close. Falls back to total when the
                      dry-run didn't compute the partition. */}
                  {(preview.summary.would_create !== undefined && preview.summary.would_link !== undefined)
                    ? (
                      <>
                        <div className="text-text">
                          {preview.summary.would_create} {preview.summary.would_create === 1 ? 'lead' : 'leads'} fresh
                          {preview.summary.would_link > 0 && (
                            <> · <span className="text-accent">{preview.summary.would_link} linked</span></>
                          )}
                        </div>
                        {preview.summary.would_link > 0 && (
                          <div className="text-text-faint text-[10px] mt-1 font-mono">
                            Linked parties already exist in Close — their lead gets this batch's tag added (no duplicate). Call history and notes stay.
                          </div>
                        )}
                      </>
                    )
                    : (
                      <div className="text-text">{preview.summary.parties} {preview.summary.parties === 1 ? 'lead' : 'leads'}</div>
                    )
                  }
                </div>
                <div className="bg-bg-input/40 border border-border rounded px-4 py-3 text-[12px]">
                  <div className="text-[10px] uppercase tracking-wider text-text-faint font-mono mb-1.5">Contact coverage</div>
                  <div className="text-text">{preview.summary.with_phone} with phone · {preview.summary.with_address} with address</div>
                  {noContactCount > 0 && (
                    <div className="text-amber mt-1">⚠ {noContactCount} {noContactCount === 1 ? 'party has' : 'parties have'} no phone or address</div>
                  )}
                </div>
              </div>

              <div className="bg-bg-input/40 border border-border rounded px-4 py-3 mb-5">
                <div className="text-[10px] uppercase tracking-wider text-text-faint font-mono mb-1.5">Tags</div>
                <div className="font-mono text-[11px] text-text-dim space-x-2">
                  {preview.tags.map(t => <span key={t} className="px-1.5 py-0.5 bg-bg-input border border-border rounded">{t}</span>)}
                </div>
              </div>

              <div className="text-[10px] uppercase tracking-wider text-text-faint font-mono mb-2">Brief preview</div>
              <div className="bg-bg-input/40 border border-border rounded mb-1">
                <div className="flex flex-wrap gap-1.5 px-3 py-2 border-b border-border">
                  {preview.briefs.map(b => (
                    <button key={b.party_id}
                      onClick={() => setOpenBriefPartyId(b.party_id)}
                      title={b.already_in_close
                        ? 'Already in Close — will tag-link, not duplicate'
                        : 'Will be created fresh in Close'}
                      className={'px-2 py-1 text-[10px] font-mono rounded border ' + (
                        openBriefPartyId === b.party_id
                          ? 'border-accent bg-accent-soft text-accent-bright'
                          : 'border-border text-text-faint hover:text-text hover:border-border-bright'
                      )}>
                      {b.already_in_close && <span className="mr-1 text-accent">●</span>}
                      {b.display_name}
                    </button>
                  ))}
                </div>
                <pre className="px-4 py-3 text-[11px] text-text-dim whitespace-pre-wrap font-mono leading-relaxed max-h-[300px] overflow-y-auto">
                  {preview.briefs.find(b => b.party_id === openBriefPartyId)?.markdown ?? '(no brief)'}
                </pre>
              </div>
            </>
          )}

          {phase === 'pushing' && (
            <div className="text-[12px] text-text-faint font-mono py-12 text-center">
              Pushing {count} {count === 1 ? 'lead' : 'leads'} to Close… don't close this window.
            </div>
          )}

          {phase === 'pushed' && pushed && (
            <div className="space-y-4">
              <div className="bg-green/10 border border-green/30 rounded px-4 py-3 text-[12px]">
                <div className="text-green text-[13px] font-medium mb-1">Push complete</div>
                <div className="text-text">
                  {pushed.summary.leads_created} {pushed.summary.leads_created === 1 ? 'lead' : 'leads'} created
                  {pushed.summary.leads_linked > 0 && (
                    <> · <span className="text-accent">{pushed.summary.leads_linked} linked</span> to existing Close leads</>
                  )}
                </div>
                {pushed.summary.leads_linked > 0 && (
                  <div className="text-text-dim text-[10px] mt-1">
                    Linked leads now appear in this Smart View alongside the freshly-created ones. No duplicates in Close.
                  </div>
                )}
                <div className="text-text-faint text-[10px] font-mono mt-1">Batch id <code className="text-text-dim">{pushed.build_batch_id.slice(0, 8)}</code> · qualify, then create opportunities manually</div>
              </div>

              {pushed.smart_view && (
                <div className="bg-accent-soft border border-accent/40 rounded px-4 py-3 text-[12px]">
                  <div className="text-accent text-[11px] uppercase tracking-wider font-mono mb-1">Smart View created</div>
                  <div className="text-text">{pushed.smart_view.name}</div>
                  <a href={pushed.smart_view.url} target="_blank" rel="noreferrer"
                    className="text-accent hover:text-accent-bright font-mono text-[11px] mt-1 inline-block">
                    Open in Close ↗
                  </a>
                </div>
              )}
              {!pushed.smart_view && pushed.smart_view_fallback_query && (
                <div className="bg-amber/10 border border-amber/30 rounded px-4 py-3 text-[11px]">
                  <div className="text-amber uppercase tracking-wider font-mono mb-1">Smart View not created</div>
                  <div className="text-text-dim">Paste this filter into Close's lead search to see the batch, then click "Save search":</div>
                  <code className="block mt-1.5 px-2 py-1 bg-bg-input border border-border rounded font-mono text-[11px] text-text select-all">{pushed.smart_view_fallback_query}</code>
                </div>
              )}

              {pushed.push_warnings.length > 0 && (
                <div className="bg-amber/10 border border-amber/30 rounded px-4 py-3 text-[11px] text-amber font-mono">
                  <div className="uppercase tracking-wider mb-1">Warnings</div>
                  {pushed.push_warnings.map((w, i) => <div key={i}>· {w}</div>)}
                </div>
              )}

              <div>
                <div className="text-[10px] uppercase tracking-wider text-text-faint font-mono mb-2">Close leads</div>
                <div className="space-y-1.5">
                  {pushed.close_leads.map(l => (
                    <div key={l.close_lead_id} className="flex items-center justify-between bg-bg-input/40 border border-border rounded px-3 py-2 text-[12px]">
                      <div className="flex items-center gap-2">
                        <span className="text-text-dim font-mono">{l.party_id.slice(0, 8)}…</span>
                        {l.linked && (
                          <span
                            title="Existing Close lead — this batch's tag was added; no duplicate created"
                            className="px-1.5 py-0 text-[9px] font-mono uppercase tracking-wider rounded border border-accent/40 bg-accent-soft text-accent"
                          >linked</span>
                        )}
                      </div>
                      <a href={l.close_lead_url} target="_blank" rel="noreferrer"
                        className="text-accent hover:text-accent-bright font-mono text-[11px]">
                        Open lead ↗
                      </a>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-[10px] text-text-faint font-mono">
                Build batch id: <span className="text-text">{pushed.build_batch_id}</span>
              </div>
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-2 bg-bg-card-soft/40 shrink-0">
          <button onClick={onClose}
            disabled={phase === 'pushing'}
            className="px-4 py-2 text-[13px] text-text-dim hover:text-text border border-border rounded disabled:opacity-50">
            {phase === 'pushed' ? 'Close' : 'Cancel'}
          </button>
          {phase === 'preview-ready' && (
            <button onClick={commitPush}
              className="px-4 py-2 text-[13px] bg-accent text-bg font-medium rounded hover:bg-accent-bright">
              Push {count} to Close
            </button>
          )}
          {phase === 'pushing' && (
            <button disabled className="px-4 py-2 text-[13px] bg-accent/40 text-bg font-medium rounded cursor-not-allowed">
              Pushing…
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
